import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Loader2, User } from "lucide-react";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/blogs`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;
const backendUrl = `${siteUrl}/seva-backend`;

type BlogItem = {
  id: number | string;
  title: string;
  slug?: string;
  category?: string;
  short_description?: string;
  cover_image?: string;
  author_name?: string;
  seo_title?: string;
  meta_description?: string;
  created_at?: string;
  updated_at?: string;
  published_at?: string;
};

const stripText = (text?: string) =>
  (text || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();

const limitText = (text: string, maxLength = 160) => {
  const clean = stripText(text);
  return clean.length <= maxLength ? clean : `${clean.slice(0, maxLength - 1).trim()}…`;
};

const getAbsoluteImage = (url?: string) => {
  if (!url) return defaultImage;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/")) return `${backendUrl}${url}`;
  return `${siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

const formatDate = (date?: string) => {
  if (!date) return "SEVAA Foundation";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const Blogs = () => {
  const [blogs, setBlogs] = useState<BlogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    fetch(`${backendUrl}/api/blogs/public-list.php`, { cache: "no-store" })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || "Blogs could not be loaded.");
        }
        return data;
      })
      .then((data) => {
        if (isMounted) setBlogs(Array.isArray(data.blogs) ? data.blogs : []);
      })
      .catch((fetchError) => {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Blogs could not be loaded.");
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${pageUrl}#collectionpage`,
    name: "SEVAA Foundation News, Stories and NGO Awareness Blogs",
    url: pageUrl,
    description:
      "Read SEVAA Foundation blogs about child education, tree plantation, hunger support, women empowerment, elderly care, medical support and blood donation awareness.",
    inLanguage: "en-IN",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SEVAA Foundation",
      url: siteUrl,
    },
    about: [
      "Child Education",
      "Tree Plantation",
      "Hunger Support",
      "Women Empowerment",
      "Homes for Old Age People",
      "Medical and Blood Donation",
    ],
    publisher: {
      "@type": "NGO",
      "@id": `${siteUrl}/#ngo`,
      name: "SEVAA Foundation",
      alternateName: "Swadesh Sevaa Charitable Foundation",
      url: siteUrl,
      logo: {
        "@type": "ImageObject",
        url: logoImage,
      },
    },
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${pageUrl}#itemlist`,
    name: "SEVAA Foundation Blog Stories",
    numberOfItems: blogs.length,
    itemListElement: blogs.map((blog, index) => {
      const identifier = blog.slug || blog.id;
      const url = `${siteUrl}/blog/${identifier}`;

      return {
        "@type": "ListItem",
        position: index + 1,
        url,
        name: blog.title,
        item: {
          "@type": "BlogPosting",
          headline: blog.title,
          description: limitText(blog.meta_description || blog.short_description || blog.title),
          image: getAbsoluteImage(blog.cover_image),
          datePublished: blog.published_at || blog.created_at,
          dateModified: blog.updated_at || blog.published_at || blog.created_at,
          author: {
            "@type": "Organization",
            name: blog.author_name || "SEVAA Foundation",
          },
        },
      };
    }),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${siteUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blogs",
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>NGO News & Social Impact Blogs | SEVAA Foundation</title>
        <meta
          name="description"
          content="Read SEVAA Foundation NGO blogs on child education, tree plantation, hunger support, women empowerment, elderly care, medical help and blood donation."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="NGO News & Social Impact Blogs | SEVAA Foundation" />
        <meta
          property="og:description"
          content="Explore useful NGO awareness articles and social impact stories from SEVAA Foundation."
        />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation NGO news and social impact blogs" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SEVAA Foundation NGO Blogs" />
        <meta
          name="twitter:description"
          content="Read useful articles about education, environment, hunger support, women empowerment, elder care and health awareness."
        />
        <meta name="twitter:image" content={defaultImage} />

        <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main className="min-h-screen bg-slate-50 px-6 py-16 font-poppins">
        <div className="mx-auto max-w-7xl">
          <header className="mx-auto mb-14 max-w-3xl text-center">
            <span className="mb-4 inline-block text-xs font-black uppercase tracking-[0.25em] text-orange-600">
              SEVAA Foundation Updates
            </span>
            <h1 className="mb-6 text-4xl font-black tracking-tight text-slate-950 md:text-6xl">
              News, Stories & NGO Awareness
            </h1>
            <p className="text-lg leading-8 text-slate-600">
              Explore useful articles connected to SEVAA Foundation&apos;s six focus areas: child education,
              tree plantation, hunger support, women empowerment, elderly care, and medical and blood donation awareness.
            </p>
          </header>

          {isLoading ? (
            <div className="flex items-center justify-center py-24 text-slate-500">
              <Loader2 className="mr-3 animate-spin text-orange-500" size={30} />
              <span className="font-semibold">Loading stories...</span>
            </div>
          ) : error ? (
            <section className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center font-semibold text-red-700">
              {error}
            </section>
          ) : blogs.length === 0 ? (
            <section className="rounded-2xl border border-slate-200 bg-white py-20 text-center shadow-sm">
              <h2 className="mb-2 text-xl font-bold text-slate-700">No blogs published yet</h2>
              <p className="text-slate-500">Please check again soon for SEVAA Foundation updates.</p>
            </section>
          ) : (
            <section aria-labelledby="blog-list-heading">
              <h2 id="blog-list-heading" className="sr-only">
                SEVAA Foundation blog articles
              </h2>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {blogs.map((blog) => {
                  const identifier = blog.slug || blog.id;
                  const publishedDate = blog.published_at || blog.created_at;

                  return (
                    <article key={blog.id} className="h-full">
                      <Link
                        to={`/blog/${identifier}`}
                        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-100"
                        aria-label={`Read ${blog.title}`}
                      >
                        <div className="relative h-56 overflow-hidden bg-slate-100">
                          <img
                            src={getAbsoluteImage(blog.cover_image)}
                            alt={`${blog.title} - SEVAA Foundation ${blog.category || "NGO"} article`}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                            loading="lazy"
                            width={600}
                            height={400}
                          />

                          {blog.category && (
                            <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-orange-700 shadow-sm backdrop-blur-sm">
                              {blog.category}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-1 flex-col p-6">
                          <h2 className="mb-3 line-clamp-2 text-xl font-black leading-7 text-slate-950 transition group-hover:text-orange-600">
                            {blog.title}
                          </h2>

                          <p className="mb-6 line-clamp-3 flex-1 leading-7 text-slate-600">
                            {blog.short_description}
                          </p>

                          <div className="mt-auto border-t border-slate-100 pt-4">
                            <div className="mb-4 flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500">
                              <time dateTime={publishedDate} className="flex items-center gap-1.5">
                                <Calendar size={14} /> {formatDate(publishedDate)}
                              </time>
                              <span className="flex items-center gap-1.5">
                                <User size={14} /> {blog.author_name || "SEVAA Foundation"}
                              </span>
                            </div>

                            <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-orange-600 transition-all group-hover:gap-3">
                              Read Article <ArrowRight size={16} />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
};

export default Blogs;
